/**
 * Maximum lengths Sage 200 accepts for each field of an order's delivery
 * address. These mirror the Del* limits in the Data Exchange schema.
 *
 * Sage rejects anything longer outright, with an error that names the internal
 * field rather than anything the user recognises:
 *
 *   Cannot set the value of the Field (.PostalName) to Halliwell Jones (...)
 *
 * so we check the lengths before exporting and tell the user which field to
 * shorten instead of letting the export fail against Sage.
 */
const sageAddressFieldLimits = [
  { field: "name", label: "name", limit: 60 },
  { field: "line_1", label: "address line 1", limit: 60 },
  { field: "line_2", label: "address line 2", limit: 60 },
  { field: "line_3", label: "address line 3", limit: 60 },
  { field: "line_4", label: "address line 4", limit: 60 },
  { field: "city", label: "city", limit: 60 },
  { field: "county", label: "county", limit: 60 },
  { field: "country", label: "country", limit: 60 },
  { field: "postcode", label: "postcode", limit: 10 },
  { field: "contact", label: "contact", limit: 50 },
  { field: "phone", label: "phone", limit: 30 },
  { field: "email", label: "email", limit: 255 },
] as const

export type ISageAddressFieldName = (typeof sageAddressFieldLimits)[number]["field"]

/**
 * Returns a `{ field: message }` map of the address fields that are too long
 * for Sage, or undefined when the address is fine to export.
 *
 * Intended for form validation, where errors are shown against the field.
 */
export function getSageAddressLengthErrors(
  address?: Partial<Record<ISageAddressFieldName, string | null | undefined>> | null,
): Partial<Record<ISageAddressFieldName, string>> | undefined {
  if (!address) {
    return undefined
  }

  const errors: Partial<Record<ISageAddressFieldName, string>> = {}
  for (const { field, limit } of sageAddressFieldLimits) {
    const value = address[field]
    if (value && value.length > limit) {
      errors[field] = `must be ${limit} characters or fewer for Sage (currently ${value.length})`
    }
  }

  return Object.keys(errors).length > 0 ? errors : undefined
}

/**
 * Returns a single sentence explaining why an address can't be exported to
 * Sage, or undefined when it can.
 *
 * Intended for the export buttons and the server side export validation, where
 * there is only one message to show.
 */
export function getSageAddressLengthError(
  address?: Partial<Record<ISageAddressFieldName, string | null | undefined>> | null,
  { addressLabel = "delivery address" }: { addressLabel?: string } = {},
): string | undefined {
  if (!address) {
    return undefined
  }

  const tooLong = sageAddressFieldLimits.filter(({ field, limit }) => {
    const value = address[field]
    return !!value && value.length > limit
  })

  if (tooLong.length === 0) {
    return undefined
  }

  const fields = tooLong
    .map(({ field, label, limit }) => `${label} (${address[field]!.length}/${limit})`)
    .join(", ")

  return `The ${addressLabel} is too long for Sage. Please shorten the ${fields} before exporting.`
}
